import { ReactNode, useEffect, useRef, useState } from "react"
import styled from "styled-components"

import circleIcon from "../assets/icons/circle.png";
import noCircleIcon from "../assets/icons/no_circle.png";
import soundIcon from "../assets/icons/sound.png";
import muteIcon from "../assets/icons/mute.png";
import clearCanvasIcon from "../assets/icons/clear_canvas.png";

import aranea from "../assets/images/doodles/aranea.png";
import blattodea from "../assets/images/doodles/blattodea.png";
import coleoptera from "../assets/images/doodles/coleoptera.png";
import dermaptera from "../assets/images/doodles/dermaptera.png";
import diptera from "../assets/images/doodles/diptera.png";
import hemiptera from "../assets/images/doodles/hemiptera.png";
import hymenoptera from "../assets/images/doodles/hymenoptera.png";
import lepidoptera_imago from "../assets/images/doodles/lepidoptera_imago.png";
import lepidoptera_larva from "../assets/images/doodles/lepidoptera_larva.png";
import mantodea from "../assets/images/doodles/mantodea.png";
import myriapoda from "../assets/images/doodles/myriapoda.png";
import neuroptera from "../assets/images/doodles/neuroptera.png";
import odonata from "../assets/images/doodles/odonata.png";
import orthoptera from "../assets/images/doodles/orthoptera.png";
import psocodea from "../assets/images/doodles/psocodea.png";
import megaloptera from "../assets/images/doodles/megaloptera.png";
import ephemeroptera from "../assets/images/doodles/ephemeroptera.png";
import phasmatodea from "../assets/images/doodles/phasmatodea.png";
import trichoptera from "../assets/images/doodles/trichoptera.png";
import plecoptera from "../assets/images/doodles/plecoptera.png";
import zygentoma from "../assets/images/doodles/zygentoma.png";
import { BaseContext, Panner3D, PolySynth, Reverb, Synth, Volume } from "tone";
import { Circle, Diamond, Doodle, fuzz, parseRGBA, Point, randBrightness, randEnds, randPastel, similarRGBA, AnimatedShape } from "../utils/CanvasUtils";
import { numToScale, octave, Scale } from "../utils/musicUtils";

const MAX_DOODLES = 5;
const doodleList = [aranea, blattodea, coleoptera, dermaptera, diptera, hemiptera, hymenoptera, lepidoptera_imago, lepidoptera_larva, mantodea, myriapoda, neuroptera, odonata, orthoptera, psocodea, megaloptera, ephemeroptera, phasmatodea, trichoptera, plecoptera, zygentoma];

const StyledCanvas = styled.div`
  /* background: pink; */
  position: relative;
  height: 100%;
  width: 100%;

  .siteCanvas {
    position: fixed;
    /* width: 100%; */
    /* height: 100%; */
    z-index: -1;
  }

  padding: 0px;
  margin: 0px;

  .canvasButtons {
    /* background-color: pink; */
    font-size: 32pt;
    display: flex;
    flex-direction: column-reverse;
    gap: 10px;

    position: fixed;
    bottom: 20px;
    right: 20px;
  }

  .copyright {
    /* background-color: pink; */
    font-size: 10pt;
    /* display: flex;
    flex-direction: column-reverse;
    gap: 10px; */

    position: fixed;
    bottom: 5px;
    left: 5px;
    /* left: 50%; */

    background: ${props => props.theme.alt};
    color: ${props => props.theme.main};
  }

  .canvasIcon {
    height: 1em;
    &:hover {
      cursor: pointer;
    }
  }

  .canvasIcon:hover + .canvasText {
    display: flex;
    /* height: 10px; */
    align-items: center;
  }

  .canvasButton {
    display: flex;
    flex-direction: row-reverse;
    gap: 20px;
    height: 1em;
    align-items: center;
  }

  .canvasText {
    background: white;
    /* backdrop-filter: blur(5px); */
    padding: 0px 10px;
    display: none;
  }
`

const MAX_CIRCLES = 10;

function Canvas({ children }: { children: ReactNode }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const PolyRef = useRef<PolySynth | null>(null);
  const Panner3DRef = useRef<Panner3D | null>(null);
  const gainRef = useRef<Volume | null>(null);
  const reverbRef = useRef<Reverb | null>(null);

  const firstShapes = useRef<boolean>(false);

  const mouseHeldDown = useRef<boolean>(false);
  const prevMousePos = useRef<Point>({ x: 0, y: 0 });

  const frame = useRef<number>(0);
  const shapes = useRef<Array<AnimatedShape>>([]);

  const doodleIdx = useRef<Array<number>>([]);
  const doodles = useRef<Array<Doodle>>([]);
  const firstClick = useRef<boolean>(false);

  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);

  const [drawable, setDrawable] = useState<boolean>(true);
  const [audible, setAudible] = useState<boolean>(true);

  const paletteRef = useRef<{ r: number, g: number, b: number, a: number } | null>(null);
  const shapeRef = useRef<string | null>(null);

  const randShape = (ctx: CanvasRenderingContext2D, event: MouseEvent, mute: boolean = false): AnimatedShape => {
    const maxPossibleR = ctx.canvas.width / 7;
    const maxR = Math.floor(Math.random() * (maxPossibleR - 25)) + 25;
    let newShape: AnimatedShape;
    if (shapeRef.current == "circle") {
      newShape = new Circle(
        event.x,
        event.y,
        maxR,
        // randColor()
        paletteRef.current ? similarRGBA(paletteRef.current.r, paletteRef.current.g, paletteRef.current.b, paletteRef.current.a, 0.1) : randPastel()
      );
    } else {
      newShape = new Diamond(
        event.x,
        event.y,
        maxR,
        // randColor()
        paletteRef.current ? similarRGBA(paletteRef.current.r, paletteRef.current.g, paletteRef.current.b, paletteRef.current.a, 0.1) : randPastel()
      );
    }

    // if (circles.current.length > MAX_CIRCLES) {
    //   // circles.current[0].destroy = true;
    // }
    shapes.current.push(newShape);
    if (!mute) makeNote(newShape, event);
    return newShape;
  }

  // RENDER LOOP
  const draw = (ctx: CanvasRenderingContext2D) => {
    // event.preventDefault();
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height)

    let i = 0;
    // while (i < circles.current.length) {
    //   if (circles.current[i].destroyed) {
    //     circles.current.splice(i, 1)
    //   } else {
    //     circles.current[i].render(ctx);
    //   }
    //   i++;
    // }
    for (let i = 0; i < shapes.current.length; i++) {
      shapes.current[i].render(ctx);
    }
    for (let i = 0; i < doodles.current.length; i++) {
      doodles.current[i].render(ctx);
    }

    frame.current++;
    requestAnimationFrame(() => draw(ctx));
  }

  const makeNote = (s: AnimatedShape, event: MouseEvent) => {
    // Play a note
    if (!PolyRef.current || !Panner3DRef.current || !canvasRef.current) return;
    const maxPossibleD = canvasRef.current.width / 7;
    const xPan = (event.x - (canvasRef.current.width / 2)) / (canvasRef.current.width / 2);
    const yPan = (event.y - (canvasRef.current.height / 2)) / (canvasRef.current.height / 2);
    const note = octave(numToScale(Math.random(), Scale.PENTATONIC, 2), Math.random() > 0.5 ? 1 : 0);
    const duration = ((s.maxD / maxPossibleD) / 2) + 0.01
    // Panner3DRef.current.setPosition(xPan, yPan, 0);
    PolyRef.current.triggerAttackRelease(note, duration);
  }

  // Set up canvas, sounds
  useEffect(() => {
    if (containerRef.current) {
      // Canvas size listens to the size of the window
      const resizeObserver = new ResizeObserver((size) => {
        let rect = size[0].contentRect;
        setWidth(rect.width);
        setHeight(rect.height);
      })
      resizeObserver.observe(containerRef.current);

      // const resizeListener = () => {
      //   setWidth(window.innerWidth);
      //   setHeight(window.innerHeight);
      // }

      // window.addEventListener("resize", resizeListener)

      gainRef.current = new Volume().toDestination();
      reverbRef.current = new Reverb(1.5).connect(gainRef.current);
      Panner3DRef.current = new Panner3D().connect(reverbRef.current);

      // ALEATORIC VISUALS AND AUDIO

      Math.random() > 0.5 ? paletteRef.current = parseRGBA(randPastel()) : paletteRef.current = null;
      let oscType: "sine" | "triangle";

      if (Math.random() > 0.5) {
        shapeRef.current = "circle";
        oscType = "sine";
      } else {
        shapeRef.current = "square";
        oscType = "triangle";
      }

      PolyRef.current = new PolySynth(Synth, { oscillator: { type: oscType }, envelope: { attack: 0.05, release: 0.1 } }).connect(Panner3DRef.current);
    }
  }, [])

  useEffect(() => {
    if (!gainRef.current) return;
    gainRef.current.mute = !audible;
  }, [audible])

  useEffect(() => {
    if (canvasRef.current) {
      ctxRef.current = canvasRef.current.getContext("2d");
      // Trigger all the canvas stuff

      if (ctxRef.current) draw(ctxRef.current);

      document.addEventListener("mousedown", mouseDownHandler);
      document.addEventListener("mousemove", mouseMoveHandler);
      document.addEventListener("mouseup", mouseUpHandler);
      document.addEventListener("mouseleave", mouseUpHandler);
      document.addEventListener("dragend", mouseUpHandler);
      document.addEventListener("dragleave", mouseUpHandler);
    }

    return () => {
      document.removeEventListener("mousedown", mouseDownHandler);
      document.removeEventListener("mousemove", mouseMoveHandler);
      document.removeEventListener("mouseup", mouseUpHandler);
      document.removeEventListener("mouseleave", mouseUpHandler);
      document.removeEventListener("dragend", mouseUpHandler);
      document.removeEventListener("dragleave", mouseUpHandler);
    }
  }, [drawable])

  // Update canvas size to always match div size
  useEffect(() => {
    if (canvasRef.current && containerRef.current) {
      canvasRef.current.width = width;
      canvasRef.current.height = height;
    }
  }, [width, height])

  const mouseDownHandler = (e: MouseEvent) => {
    // e.preventDefault();
    if (ctxRef.current && drawable) {
      // draw(ctxRef.current, e);
      // Draw + animate a circle on mouse click

      mouseHeldDown.current = true;
      prevMousePos.current = { x: e.x, y: e.y };

      if (!firstClick.current) {
        const numDoodles = Math.min(Math.floor(Math.random() * (MAX_DOODLES - 1)) + 1, doodleList.length);
        // console.log("total num doodles: " + numDoodles);
        for (let i = 0; i < numDoodles; i++) {
          let idx = Math.floor(Math.random() * doodleList.length);
          while (doodleIdx.current.includes(idx)) {
            idx = Math.floor(Math.random() * doodleList.length);
          }
          doodleIdx.current.push(idx);
        }
        const w = ctxRef.current.canvas.width;
        const h = ctxRef.current.canvas.height;
        for (let i = 0; i < doodleIdx.current.length; i++) {
          let doodleX = randEnds() * (w * 0.8) + (w * 0.1);
          let doodleY = randEnds() * (h * 0.8) + (h * 0.1);
          // Make sure the doodles are sufficiently spaced out
          while (doodles.current.some((el) => Math.sqrt(Math.pow(doodleX - el.x, 2) + Math.pow(doodleY - el.y, 2)) < (w / 5))) {
            doodleX = randEnds() * (w * 0.8) + (w * 0.1);
            doodleY = randEnds() * (h * 0.8) + (h * 0.1);
          }
          const newDoodle = new Doodle(doodleX, doodleY, doodleList[doodleIdx.current[i]]);
          doodles.current.push(newDoodle);
        }
        firstClick.current = true;
      }

      randShape(ctxRef.current, e);
    }
  }

  const mouseMoveHandler = (e: MouseEvent) => {
    // TODO: Redo this only happen in non-mobile in the actual middle of the div, on page load.
    // Draw some circles on the first page to fill in the empty space on the left
    if (ctxRef.current && canvasRef.current && !firstShapes.current) {
      const w = canvasRef.current.width;
      const h = canvasRef.current.height;

      for (let i = 0; i < 3; i++) {
        let autoShape = new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          clientX: fuzz((w / 4) + (i % 2 === 0 ? i : i * (w / 10)), 0.1),
          clientY: fuzz((h * 2 / 5) + (i * (h / 7)), 0.05)
        })
        randShape(ctxRef.current, autoShape, true);
      }
      firstShapes.current = true;
    }
    if (mouseHeldDown.current && ctxRef.current) {
      const currPos = { x: e.x, y: e.y };
      const dist = Math.sqrt(
        Math.pow(currPos.x - prevMousePos.current.x, 2) +
        Math.pow(currPos.y - prevMousePos.current.y, 2)
      );

      if (dist > Math.min(ctxRef.current.canvas.width / 10, ctxRef.current.canvas.height / 10)) {
        randShape(ctxRef.current, e);
        prevMousePos.current = currPos;
      }
    }
  }

  const mouseUpHandler = () => {
    mouseHeldDown.current = false;
  }

  const clearCanvas = () => {
    if (ctxRef.current && canvasRef.current) {
      ctxRef.current.fillStyle = "white";
      ctxRef.current.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height)
      shapes.current = [];
    }
  }

  return (
    <StyledCanvas ref={containerRef}>
      <canvas ref={canvasRef} height={"100%"} width={"100%"} className={"siteCanvas"}></canvas>
      {children}
      <div className={"copyright"}>
        Coded with love, sweat, & tears © {new Date().getFullYear()}
      </div>
      <div className={"canvasButtons"}>
        <div className={"canvasButton"}>
          <img className="canvasIcon" onClick={clearCanvas} src={clearCanvasIcon} alt={"clear canvas"} />
          <div className={"canvasText"}>clear</div>
        </div>

        <div className="canvasButton">
          <div className={"canvasIcon"}>
            {drawable ? <img className={"canvasIcon"} src={circleIcon} alt={"circles: on"} onClick={() => setDrawable(!drawable)} />
              : <img className={"canvasIcon"} src={noCircleIcon} alt={"circles: off"} onClick={() => setDrawable(!drawable)} />}
          </div>
          <div className={"canvasText"}>{drawable ? "draw: on" : "draw: off"}</div>
        </div>

        <div className="canvasButton">
          <div className={"canvasIcon"}>
            {audible ? <img className={"canvasIcon"} src={soundIcon} alt={"sound: on"} onClick={() => setAudible(!audible)} />
              : <img className={"canvasIcon"} src={muteIcon} alt={"sound: off"} onClick={() => setAudible(!audible)} />}
          </div>
          <div className={"canvasText"}>{audible ? "sound: on" : "sound: off"}</div>
        </div>
      </div>
    </StyledCanvas>
  )
}

export default Canvas